import React from 'react';
import PropTypes from 'prop-types';
import { Route, useParams, useRouteMatch, useLocation, useHistory } from 'react-router-dom';
import { WithAuthentication } from '@twigeducation/oidc-client-react';
import Layout from './Layout';
import LayoutWithSwoosh from './LayoutWithSwoosh';
import LayoutWithStickyHeader from './LayoutWithStickyHeader';
import UpdatePasswordRequired from '../components/UpdatePasswordRequired';
import './scss/LayoutRoute.scss';

const AuthenticatedLayoutRouteContent = ({
    ComponentToRender,
    roles,
    getRedirects,
    withConstellation,
    withFooter,
    withHeader,
    LayoutComponent,
    ...props
}) => {
    const params = useParams();
    const match = useRouteMatch();
    const location = useLocation();
    const history = useHistory();

    return (
        <WithAuthentication roles={roles} redirects={getRedirects(params)} {...props}>
            <UpdatePasswordRequired>
                <LayoutComponent
                    component={ComponentToRender}
                    withConstellation={withConstellation}
                    withFooter={withFooter}
                    withHeader={withHeader}
                    {...props}
                    match={match}
                    location={location}
                    history={history}
                />
            </UpdatePasswordRequired>
        </WithAuthentication>
    );
};

const AuthenticatedLayoutRoute = ({
    component: Component,
    children,
    roles,
    withConstellation,
    withFooter,
    withHeader,
    withStickyHeader,
    withSwoosh,
    getRedirects,
    ...props
}) => {
    let LayoutComponent = Layout;
    if (withSwoosh) {
        LayoutComponent = LayoutWithSwoosh;
    }
    if (withStickyHeader) {
        LayoutComponent = LayoutWithStickyHeader;
    }

    // Support both component prop (legacy) and children (v5.1 style)
    const ComponentToRender = Component || children?.type;

    return (
        <Route {...props}>
            <AuthenticatedLayoutRouteContent
                ComponentToRender={ComponentToRender}
                roles={roles}
                getRedirects={getRedirects}
                withConstellation={withConstellation}
                withFooter={withFooter}
                withHeader={withHeader}
                LayoutComponent={LayoutComponent}
                {...props}
            />
        </Route>
    );
};

AuthenticatedLayoutRoute.defaultProps = {
    getRedirects: () => [{ roles: [], redirectUrl: '' }],
    roles: [],
    withConstellation: true,
    withFooter: true,
    withHeader: true,
    withStickyHeader: false,
    withSwoosh: false,
};

AuthenticatedLayoutRoute.propTypes = {
    component: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
    children: PropTypes.element,
    roles: PropTypes.arrayOf(PropTypes.string),
    withConstellation: PropTypes.bool,
    withFooter: PropTypes.bool,
    withHeader: PropTypes.bool,
    withStickyHeader: PropTypes.bool,
    withSwoosh: PropTypes.bool,
    getRedirects: PropTypes.func,
};

export default AuthenticatedLayoutRoute;
